import React, { useCallback } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AlbumCard from "../Components/AlbumCard";
import LoadError from "../Components/LoadError";
import Photo from "../Components/Photo";
import PhotoGrid from "../Components/PhotoGrid";
import PhotoViewer from "../Components/PhotoViewer";
import { countPhotos, formatAlbumDates, getGallery, peekGallery } from "../utils/imageScanner";
import useRemoteData from "../utils/useRemoteData";

function BackLink({ to, children }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 py-1 text-sm text-base-content/60 hover:text-base-content">
      <span aria-hidden="true">←</span> {children}
    </Link>
  );
}

function PageHeader({ back, title, meta, description }) {
  return (
    <header className="pb-5 pt-4 text-left sm:pb-8 sm:pt-8">
      {back}
      <h1 className="mt-1 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
      {meta && <div className="mt-1 text-sm text-base-content/60">{meta}</div>}
      {description && <p className="mt-3 max-w-2xl text-base-content/80">{description}</p>}
    </header>
  );
}

// Every album in the collection as a swipeable row of chips, for jumping between them.
function AlbumChips({ albums, current, categorySlug }) {
  return (
    <nav aria-label="Albums" className="no-scrollbar -mx-3 mb-4 flex gap-2 overflow-x-auto px-3 sm:mx-0 sm:px-0">
      {albums.map((a) => (
        <Link
          key={a.id}
          to={`/gallery/${categorySlug}/${a.slug}`}
          aria-current={a.id === current.id ? "page" : undefined}
          className={`btn btn-sm flex-none rounded-full font-medium ${a.id === current.id ? "btn-neutral" : "btn-ghost bg-base-200"}`}
        >
          {a.name}
        </Link>
      ))}
    </nav>
  );
}

function NextAlbum({ album, categorySlug }) {
  return (
    <Link
      to={`/gallery/${categorySlug}/${album.slug}`}
      className="group mt-10 flex items-center gap-4 rounded-xl bg-base-200 p-3 text-left transition-colors hover:bg-base-300"
    >
      <div className="relative h-20 w-16 flex-none overflow-hidden rounded-md bg-base-300">
        <Photo cover src={album.cover_image_url} srcSet={album.cover_srcset} sizes="64px" alt="" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wide text-base-content/60">Next album</div>
        <div className="truncate text-lg font-semibold">{album.name}</div>
        <div className="text-sm text-base-content/60">{countPhotos(album.images.length)}</div>
      </div>
      <span aria-hidden="true" className="text-xl transition-transform group-hover:translate-x-1">→</span>
    </Link>
  );
}

function GallerySkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading photos">
      <div className="pb-5 pt-4 sm:pb-8 sm:pt-8">
        <div className="shimmer h-4 w-24 rounded" />
        <div className="shimmer mt-3 h-8 w-56 rounded" />
        <div className="shimmer mt-3 h-4 w-full max-w-md rounded" />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="shimmer aspect-[4/5] rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function DynamicGallery() {
  const { categorySlug, albumSlug } = useParams();
  const { data: gallery, error, retry } = useRemoteData(getGallery, peekGallery, categorySlug);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const album = albumSlug && gallery ? gallery.albums.find((a) => a.slug === albumSlug) : null;
  let shownImages = [];
  let looseImages = [];
  if (gallery) {
    const albumIds = new Set(gallery.albums.map((a) => a.id));
    looseImages = gallery.images.filter((img) => !albumIds.has(img.album_id));
    // The photos the viewer swipes through: the album's, or the ones listed on this page.
    shownImages = album ? album.images : gallery.albums.length > 0 ? looseImages : gallery.images;
  }

  // The open photo lives in the URL (?photo=id), so a photo can be shared and the
  // phone's back button closes the viewer instead of leaving the page.
  const photoId = searchParams.get("photo");
  const viewerIndex = photoId ? shownImages.findIndex((img) => img.id === photoId) : -1;
  const openViewer = (index) =>
    setSearchParams({ photo: shownImages[index].id }, { state: { openedHere: true } });
  const closeViewer = useCallback(() => {
    if (location.state?.openedHere) navigate(-1);
    else setSearchParams({}, { replace: true });
  }, [location.state, navigate, setSearchParams]);
  const showPhoto = (index) => {
    if (shownImages[index].id !== photoId) {
      setSearchParams({ photo: shownImages[index].id }, { replace: true, state: location.state });
    }
  };

  const container = "mx-auto max-w-6xl px-3 pb-8 sm:px-6";

  if (error) {
    return <div className={container}><LoadError onRetry={retry} /></div>;
  }
  if (!gallery) {
    return <div className={container}><GallerySkeleton /></div>;
  }

  const { category, albums, images } = gallery;
  let content;
  if (!category || (albumSlug && !album)) {
    content = (
      <div className="py-16 text-center text-base-content/70">
        <p>{category ? "This album doesn't exist." : "This collection doesn't exist."}</p>
        <Link className="btn btn-outline mt-4" to={category ? `/gallery/${categorySlug}` : "/"}>
          {category ? `Back to ${category.name}` : "See all collections"}
        </Link>
      </div>
    );
  } else if (album) {
    const dates = formatAlbumDates(album.date_from, album.date_to);
    const count = countPhotos(album.images.length);
    const position = albums.indexOf(album);
    const next = albums.length > 1 ? albums[(position + 1) % albums.length] : null;
    content = (
      <>
        <PageHeader
          back={<BackLink to={`/gallery/${categorySlug}`}>{category.name}</BackLink>}
          title={album.name}
          meta={dates ? `${dates} · ${count}` : count}
          description={album.description}
        />
        {albums.length > 1 && <AlbumChips albums={albums} current={album} categorySlug={categorySlug} />}
        <PhotoGrid images={album.images} alt={album.name} onOpen={openViewer} />
        {next && <NextAlbum album={next} categorySlug={categorySlug} />}
      </>
    );
  } else {
    const meta = [albums.length > 0 && `${albums.length} ${albums.length === 1 ? "album" : "albums"}`, countPhotos(images.length)]
      .filter(Boolean)
      .join(" · ");
    content = (
      <>
        <PageHeader
          back={<BackLink to="/">All collections</BackLink>}
          title={category.name}
          meta={images.length > 0 ? meta : null}
          description={category.description}
        />
        {images.length === 0 && (
          <p className="py-12 text-center text-base-content/70">No photos in this collection yet.</p>
        )}
        {albums.length > 0 && (
          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
            {albums.map((a, i) => (
              <AlbumCard key={a.id} album={a} link={`/gallery/${categorySlug}/${a.slug}`} priority={i < 2} />
            ))}
          </div>
        )}
        {albums.length > 0 && looseImages.length > 0 && (
          <h2 className="mb-4 mt-10 text-left text-xl font-semibold">More photos</h2>
        )}
        {shownImages.length > 0 && <PhotoGrid images={shownImages} alt={category.name} onOpen={openViewer} />}
      </>
    );
  }

  return (
    <div className={container}>
      {content}
      {viewerIndex >= 0 && (
        <PhotoViewer
          images={shownImages}
          initialIndex={viewerIndex}
          alt={album?.name || category?.name || ""}
          onIndexChange={showPhoto}
          onClose={closeViewer}
        />
      )}
    </div>
  );
}

export default DynamicGallery;
